import React, { Fragment } from 'react'
import { Box, Stack, Text } from '@machobear/design-system'
import { Layer, Source } from 'react-map-gl/maplibre'

import { colors } from '@/styles'
import {
  Bus,
  Coffee,
  GraduationCap,
  MapBuilding,
  MapHouse,
  PointFilledSecondary,
  ShoppingBag,
  ShoppingCart,
  Trees,
  Utensils
} from '@/components/icons'
import { FlyToBounds } from '@/components/map/fly-to-bounds'
import Popup from '@/components/map/popup'
import Map from '@/components/maplibre-map/map'
import Marker from '@/components/maplibre-map/marker'

import { Listing } from '../types'
import CenterOnBuilding from './center-on-building'
import {
  IconWrapper,
  MapContainer,
  MarkerContainer,
  MarkerWrapper,
  StyledPoint,
  StyledPointFilled
} from './map-component.styles'
import * as styles from './map.css'

const getLocation = (data: {
  latitude: string
  longitude: string
}): [number, number] => [Number(data.latitude), Number(data.longitude)]

const getIcon = (variant: number) => {
  switch (variant) {
    case 0:
      return <Bus style={{ position: 'absolute' }} width={14} />
    case 1:
      return <GraduationCap width={14} />
    case 2:
      return <ShoppingCart width={14} />
    case 3:
      return <Coffee width={14} />
    case 4:
      return <Utensils width={14} />
    case 5:
      return <Trees width={14} />
    case 6:
      return <ShoppingBag width={14} />
    default:
      return <MapHouse width={14} />
  }
}

const MapComponent = ({
  currentPoint,
  setCurrentPoint,
  positions = [],
  tabIndex,
  setDefaultCurrentPoint,
  setActiveId,
  listing,
  isHidden,
  icon,
  customBuildingColor,
  customPoiColor,
  isListingLocked = false
}: any): any => {
  const hasLatLon = currentPoint?.latitude && currentPoint?.longitude
  const buildingObj = hasLatLon
    ? currentPoint
    : {
        latitude: null,
        longitude: null
      }
  const hasAddress = (currentPoint?.address || '').trim().length !== 0

  const MarkerComponent = () => {
    return (
      <MarkerContainer>
        <MarkerWrapper style={{ color: customPoiColor || '#0D3880' }}>
          <IconWrapper>{getIcon(tabIndex)}</IconWrapper>
          <StyledPointFilled />
        </MarkerWrapper>
      </MarkerContainer>
    )
  }

  const BuildingComponent = () => {
    return (
      <MarkerContainer>
        <MarkerWrapper style={{ color: customBuildingColor || '#051A49' }}>
          {!isHidden && (
            <>
              <IconWrapper>
                {icon === 'building' ? (
                  <MapBuilding width={20} style={{ color: colors.white }} />
                ) : (
                  <MapHouse width={20} style={{ color: colors.white }} />
                )}
              </IconWrapper>
              <PointFilledSecondary />
            </>
          )}
        </MarkerWrapper>
      </MarkerContainer>
    )
  }

  const ActiveComponent = () => {
    return (
      <MarkerContainer>
        <MarkerWrapper>
          <IconWrapper style={{ color: colors.black }}>
            {getIcon(tabIndex)}
          </IconWrapper>
          <StyledPoint />
        </MarkerWrapper>
      </MarkerContainer>
    )
  }

  if (typeof window === 'undefined') return null

  const handleClickPoint = (point: any, index: number) => {
    setCurrentPoint({
      name: point?.info?.name,
      address: point?.info?.address,
      distance_walktime: point?.distance_walktime,
      distance: point?.distance,
      latitude: point.latitude,
      longitude: point.longitude
    })
    setActiveId(index)
  }

  const handleClickListing = (listing: Listing) => {
    setCurrentPoint({
      name: listing.name,
      address: listing.address,
      distance_walktime: '0',
      distance: '0',
      latitude: listing.lat,
      longitude: listing.lng
    })
    setActiveId(null)
  }

  const circleGeoJson = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [Number(listing.lng), Number(listing.lat)] // [longitude, latitude]
        },
        properties: {}
      }
    ]
  }

  return (
    <MapContainer>
      <Map
        center={getLocation(buildingObj)}
        scrollZoom={false}
        navigationControl={{
          enabled: true,
          compass: false,
          position: 'bottom-right'
        }}
        zoom={14}
      >
        {hasLatLon ? (
          <FlyToBounds
            centerPoint={{
              lat: currentPoint.latitude,
              lon: currentPoint.longitude
            }}
            zoom={14}
          />
        ) : (
          <FlyToBounds
            centerPoint={{ lat: listing.lat, lon: listing.lng }}
            zoom={14}
          />
        )}
        {isHidden ? (
          <Source type="geojson" data={circleGeoJson}>
            <Layer
              type="circle"
              id="circle-layer"
              paint={{
                'circle-radius': 65,
                'circle-color': colors.blue40,
                'circle-stroke-color': colors.blue40,
                'circle-stroke-width': 1,
                'circle-opacity': 0.2
              }}
            />
          </Source>
        ) : (
          <Marker
            position={getLocation(buildingObj)}
            IconComponent={ActiveComponent}
          />
        )}
        {isHidden && currentPoint?.latitude !== listing.lat && (
          <Marker
            position={getLocation(buildingObj)}
            IconComponent={ActiveComponent}
          />
        )}
        <Marker
          position={getLocation({
            latitude: listing.lat,
            longitude: listing.lng
          })}
          IconComponent={BuildingComponent}
          onClick={() => handleClickListing(listing)}
        />
        {positions.map((point: any, index: number) => (
          <Fragment key={`${tabIndex}-${point.info.name}-${index}`}>
            {point.latitude !== currentPoint?.latitude && (
              <Marker
                position={getLocation({
                  latitude: point.latitude,
                  longitude: point.longitude
                })}
                IconComponent={MarkerComponent}
                onClick={() => handleClickPoint(point, index)}
              />
            )}
          </Fragment>
        ))}
        {currentPoint && !isListingLocked && (
          <Popup
            anchor="bottom"
            latitude={currentPoint.latitude}
            longitude={currentPoint.longitude}
            closeButton={false}
            closeOnClick={false}
            offset={[6, -35] as [number, number]}
            maxWidth="max-content"
            className={styles.popup}
          >
            <Box
              background="bodyWhite"
              borderRadius="standard"
              border="borderField"
              padding="small"
            >
              <Stack space="xsmall">
                <Box display="flex" gap="xxsmall">
                  <Text weight="medium" size="small">
                    {currentPoint.name || currentPoint.address}
                  </Text>
                  {currentPoint.latitude !== listing.lat && (
                    <Text size="small" weight="regular">
                      {(currentPoint?.distance / 1000).toFixed(1)} km
                    </Text>
                  )}
                </Box>
                {hasAddress ? (
                  <Text size="xsmall">{currentPoint.address}</Text>
                ) : null}
              </Stack>
            </Box>
          </Popup>
        )}
        <CenterOnBuilding
          center={{ lat: listing.lat, lng: listing.lng }}
          setDefaultCurrentPoint={setDefaultCurrentPoint}
        />
      </Map>
    </MapContainer>
  )
}

export default MapComponent
